import React from "react";
import {
  Card,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  IconButton,
  Button,
  Typography,
  Icon,
  Checkbox,
} from "@mui/material";
import { Link } from "react-router-dom";
import { Box } from "@mui/system";
import PerfectScrollbar from "react-perfect-scrollbar";
import { DeleteRounded } from "@mui/icons-material";
import { ZoneFileItem } from "../../api/zoneFile";
import formatDate from "../../utils/formatDate";
import { Loadable } from "../Loading/Loadable";
import LoadingWrapper from "../Loading/LoadingWrapper";
import LoadingTableRow from "../Loading/LoadingTableRow";
import DialogBox from "../DialogBox";
import useApiClient from "../../api/useApiClient";

interface ZoneSetZoneFileListProps {
  zoneFiles: Loadable<{ external_id: string; name: string }[]>;
  onAssociateZoneFiles: (zoneFileIds: string[]) => Promise<void>;
  onDisassociateZoneFile: (zoneFileId: string) => Promise<void>;
}

const ZoneSetZoneFileList = ({
  zoneFiles,
  onAssociateZoneFiles,
  onDisassociateZoneFile,
}: ZoneSetZoneFileListProps) => {
  const client = useApiClient();
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [page, setPage] = React.useState(0);
  const [allZoneFiles, setAllZoneFiles] = React.useState<
    ZoneFileItem[] | undefined
  >();
  const [selectedZoneFileExternalIds, setSelectedZoneFileExternalIds] =
    React.useState<string[]>([]);
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleAssociateZoneFileSelectOne = (
    _: React.ChangeEvent<HTMLInputElement>,
    id: string
  ) => {
    const selectedIndex = selectedZoneFileExternalIds.indexOf(id);
    let newSelectedZoneFileIds: string[] = [];

    if (selectedIndex === -1) {
      newSelectedZoneFileIds = newSelectedZoneFileIds.concat(
        selectedZoneFileExternalIds,
        id
      );
    } else if (selectedIndex === 0) {
      newSelectedZoneFileIds = newSelectedZoneFileIds.concat(
        selectedZoneFileExternalIds.slice(1)
      );
    } else if (selectedIndex === selectedZoneFileExternalIds.length - 1) {
      newSelectedZoneFileIds = newSelectedZoneFileIds.concat(
        selectedZoneFileExternalIds.slice(0, -1)
      );
    } else if (selectedIndex > 0) {
      newSelectedZoneFileIds = newSelectedZoneFileIds.concat(
        selectedZoneFileExternalIds.slice(0, selectedIndex),
        selectedZoneFileExternalIds.slice(selectedIndex + 1)
      );
    }

    setSelectedZoneFileExternalIds(newSelectedZoneFileIds);
  };

  const handleAssociateZoneFileSelectAll = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked) {
      setSelectedZoneFileExternalIds(
        allZoneFiles
          ?.filter(
            (x) =>
              !zoneFiles?.data?.find((y) => y.external_id === x.external_id)
          )
          .map((zoneFile) => zoneFile.external_id) || []
      );
    } else {
      setSelectedZoneFileExternalIds([]);
    }
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | undefined
  ) => {
    const newPageLimit = parseInt(event?.target.value || "", 10);

    setRowsPerPage(isNaN(newPageLimit) ? 25 : newPageLimit);
    setPage(0);
  };

  const handlePageChange = (
    _: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  zoneFiles?.data?.sort((a, b) => (a.name > b.name ? 1 : -1));
  allZoneFiles?.sort((a, b) => (a.name > b.name ? 1 : -1));

  return (
    <Card>
      <CardContent>
        <PerfectScrollbar>
          <Box>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    Zone Files
                    <IconButton
                      title="Associate zone files"
                      color="success"
                      onClick={() => {
                        client.zone.file
                          .getAll()
                          .then((x) => setAllZoneFiles(x.zone_files))
                          .catch((err) => console.error(err));
                        handleOpen();
                      }}
                    >
                      <Icon>add_circle</Icon>
                    </IconButton>
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <LoadingWrapper
                  loadingInfo={zoneFiles}
                  loadingComponent={<LoadingTableRow colSpan={2} />}
                >
                  {zoneFiles?.data
                    ?.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage
                    )
                    ?.map((zoneFile) => (
                      <TableRow hover key={zoneFile.external_id}>
                        <TableCell>
                          <Link to={`/app/zoneFile/${zoneFile.external_id}`}>
                            {zoneFile.name}
                          </Link>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            aria-label="disassociate"
                            title="Remove zone file association"
                            color="error"
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Are you sure you want to disassociate ${zoneFile.external_id} zone file?`
                                )
                              ) {
                                void onDisassociateZoneFile(
                                  zoneFile.external_id
                                );
                              }
                            }}
                          >
                            <DeleteRounded />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </LoadingWrapper>
              </TableBody>
            </Table>
          </Box>
        </PerfectScrollbar>
        <TablePagination
          component="div"
          count={Number(zoneFiles?.data?.length) || 0}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
        <DialogBox
          open={open}
          onClose={handleClose}
          title="Associate Zone Files"
          description="Select the zone files to associate."
        >
          <PerfectScrollbar>
            <Box>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox onChange={handleAssociateZoneFileSelectAll} />
                    </TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Date Created</TableCell>
                    <TableCell>Date Updated</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allZoneFiles
                    ?.filter(
                      (x) =>
                        !zoneFiles?.data?.find(
                          (y) => y.external_id === x.external_id
                        )
                    )
                    .map((zoneFile) => (
                      <TableRow key={zoneFile.external_id}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={
                              selectedZoneFileExternalIds.indexOf(
                                zoneFile.external_id
                              ) !== -1
                            }
                            onChange={(event) =>
                              handleAssociateZoneFileSelectOne(
                                event,
                                zoneFile.external_id
                              )
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              alignItems: "center",
                              display: "flex",
                            }}
                          >
                            <Typography color="textPrimary" variant="body1">
                              {zoneFile.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{zoneFile.description}</TableCell>
                        <TableCell>
                          {formatDate(zoneFile.created_date_utc)}
                        </TableCell>
                        <TableCell>
                          {formatDate(zoneFile.last_modified_date_utc)}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
              <Button
                sx={{ m: 2 }}
                variant="contained"
                disabled={!selectedZoneFileExternalIds?.length}
                onClick={() => {
                  void onAssociateZoneFiles(selectedZoneFileExternalIds).then(
                    () => {
                      setSelectedZoneFileExternalIds([]);
                      handleClose();
                    }
                  );
                }}
              >
                Save
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  setSelectedZoneFileExternalIds([]);
                  handleClose();
                }}
              >
                Cancel
              </Button>
            </Box>
          </PerfectScrollbar>
        </DialogBox>
      </CardContent>
    </Card>
  );
};

export default ZoneSetZoneFileList;
